import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Plus, Search, Trash2, Users } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import { getSuppliers, createSupplier, deleteSupplier } from "../../lib/metalApi";

const EASE = [0.16, 1, 0.3, 1];

export const FornitoriTab = () => {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await getSuppliers();
      setItems(Array.isArray(data) ? data : data.items ?? []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onAdd = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await createSupplier(name.trim());
      setName("");
      await load();
    } catch {
      // silenzioso
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(t.reserved.confirmDelete)) return;
    try {
      await deleteSupplier(id);
      setItems((prev) => prev.filter((s) => s.id !== id));
    } catch {
      // silenzioso
    }
  };

  const filtered = items.filter((s) =>
    (s.name || "").toLowerCase().includes(q.trim().toLowerCase())
  );

  return (
    <div data-testid="fornitori-tab">
      <form onSubmit={onAdd} className="mb-6 flex gap-3">
        <input
          data-testid="supplier-add-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t.reserved.supplierNamePh}
          className="h-12 flex-1 rounded-2xl border border-[#342C55] bg-[#221D3F] px-4 font-manrope text-sm text-[#F5F5F5] placeholder:text-[#8B87A8] focus:border-[#34D399] focus:outline-none"
        />
        <button
          data-testid="supplier-add-button"
          type="submit"
          disabled={saving || !name.trim()}
          className="flex h-12 items-center gap-2 rounded-2xl bg-[#34D399] px-5 font-manrope text-sm font-bold text-[#17122E] transition-colors duration-300 hover:bg-[#2BBD8C] disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          {t.reserved.addSupplier}
        </button>
      </form>

      <div className="relative mb-5">
        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8B87A8]" />
        <input
          data-testid="supplier-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t.reserved.searchSupplierPh}
          className="h-12 w-full rounded-2xl border border-[#342C55] bg-[#221D3F] pl-11 pr-4 font-manrope text-sm text-[#F5F5F5] placeholder:text-[#8B87A8] focus:border-[#34D399] focus:outline-none"
        />
      </div>

      {loading && <p className="py-10 text-center font-manrope text-sm text-[#8B87A8]">…</p>}
      {error && (
        <p className="rounded-2xl border border-[#34D399]/40 bg-[#34D399]/10 px-4 py-3 font-manrope text-sm text-[#34D399]">
          {t.reserved.dataError}
        </p>
      )}
      {!loading && !error && items.length === 0 && (
        <p className="py-10 text-center font-manrope text-sm text-[#8B87A8]">{t.reserved.emptySuppliers}</p>
      )}
      {!loading && !error && items.length > 0 && filtered.length === 0 && (
        <p data-testid="supplier-no-results" className="py-10 text-center font-manrope text-sm text-[#8B87A8]">
          {t.reserved.emptySuppliers}
        </p>
      )}

      <div className="space-y-3">
        {filtered.map((s, i) => (
          <motion.div
            key={s.id ?? i}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: Math.min(i, 10) * 0.04, ease: EASE }}
            data-testid={`supplier-row-${i}`}
            className="flex items-center justify-between gap-3 rounded-2xl border border-[#342C55] bg-[#221D3F] px-5 py-4"
          >
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#34D399]/15">
                <Users className="h-4 w-4 text-[#34D399]" />
              </span>
              <p className="truncate font-manrope text-sm font-semibold text-[#F5F5F5]">{s.name}</p>
            </div>
            <button
              data-testid={`supplier-delete-${i}`}
              onClick={() => onDelete(s.id)}
              aria-label={t.reserved.delete}
              className="rounded-lg p-1.5 text-[#8B87A8] transition-colors duration-300 hover:bg-[#F87171]/10 hover:text-[#F87171]"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </motion.div>
        ))}
      </div>
    </div>
  );
};
